import * as React from 'react';

import { tv, type VariantProps } from '@benepass/tailwind-config-web/tailwind-variants';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore error with icons library
import { Close as CloseIcon } from '@benepass/icons';
import { AnimatePresence, motion } from 'framer-motion';

import Primitive from '../primitives';
import Close from './close.component';

import { cn } from '../../utils';

const overlayStyles = tv({
  base: [
    'bg-grayscale-100/75',
    'fixed inset-0',
    'z-40',
    'transition-opacity',
    'data-[state=open]:animate-in data-[state=closed]:animate-out',
    'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
  ],
});

const contentStyles = tv({
  base: [
    'bg-white',
    'fixed z-50',
    'flex flex-col',
    'shadow-lg',
    'animate-in fade-in-80',
    'data-[state=open]:animate-in data-[state=closed]:animate-out',
    'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
  ],
  variants: {
    position: {
      center: [
        'top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2',
        'min-h-fill-available md:min-h-0',
        'pb-7',
        'md:max-w-xl md:w-11/12 max-h-screen md:max-h-90vh w-full',
        'rounded-md',
      ],
      left: ['w-5/12', 'top-0 bottom-0 left-0', 'rounded-none py-5 px-10'],
      right: ['w-5/12', 'top-0 bottom-0 right-0', 'rounded-none py-5 px-10'],
    },
    size: {
      sm: 'max-w-sm',
      md: 'max-w-md',
      lg: 'max-w-lg',
      xl: 'max-w-xl',
      '2xl': 'max-w-2xl',
      '3xl': 'max-w-3xl',
      '4xl': 'max-w-4xl',
      '5xl': 'max-w-5xl',
      full: 'max-w-full',
    },
  },
  defaultVariants: {
    position: 'center',
    size: 'xl',
  },
});

export type ContentProps = React.ComponentPropsWithoutRef<typeof Primitive.Dialog.Content> &
  VariantProps<typeof contentStyles> & {
    hideCloseButton?: boolean;
    hideHeader?: boolean;
    overlayClassName?: string;
    contentClassName?: string;
    titleContainerClassName?: string;
    animate?: boolean;
  };

const ContentWrapper = React.forwardRef<React.ElementRef<typeof Primitive.Dialog.Content>, ContentProps>(
  ({ children, animate, ...props }, ref) => {
    const { position, size, className, ...rest } = props;

    if (animate) {
      return (
        <Primitive.Dialog.Content {...rest} ref={ref} asChild>
          <motion.div
            key="modal-content"
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.5 }}
            className={cn(contentStyles({ position, size, className }))}
            layout
          >
            {children}
          </motion.div>
        </Primitive.Dialog.Content>
      );
    }

    return (
      <Primitive.Dialog.Content {...rest} ref={ref} className={cn(contentStyles({ position, size, className }))}>
        {children}
      </Primitive.Dialog.Content>
    );
  }
);

ContentWrapper.displayName = 'Modal.ContentWrapper';

const Overlay = React.forwardRef<
  React.ElementRef<typeof Primitive.Dialog.Overlay>,
  React.ComponentPropsWithoutRef<typeof Primitive.Dialog.Overlay> & { animate?: boolean }
>(({ animate, ...props }, ref) => {
  if (animate) {
    return (
      <AnimatePresence>
        <motion.div
          key="modal-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-40"
        >
          <Primitive.Dialog.Overlay ref={ref} {...props} />
        </motion.div>
      </AnimatePresence>
    );
  }

  return <Primitive.Dialog.Overlay ref={ref} {...props} />;
});

Overlay.displayName = 'Modal.Overlay';

const Content: React.ForwardRefExoticComponent<
  ContentProps & React.RefAttributes<React.ElementRef<typeof Primitive.Dialog.Content>>
> = React.forwardRef<React.ElementRef<typeof Primitive.Dialog.Content>, ContentProps>(
  (
    {
      position,
      size,
      hideCloseButton = false,
      hideHeader = false,
      contentClassName,
      titleContainerClassName,
      className,
      children,
      animate = false,
      ...props
    },
    ref
  ) => {
    const titleElement = React.Children.toArray(children).find(
      (child) =>
        React.isValidElement(child) && child.type && (child.type as React.ComponentType).displayName === 'Modal.Title'
    );

    const otherElements = React.Children.toArray(children).filter(
      (child) =>
        React.isValidElement(child) && child.type && (child.type as React.ComponentType).displayName !== 'Modal.Title'
    );

    const showTitle = !hideHeader && (titleElement || !hideCloseButton);

    return (
      <Primitive.Dialog.Portal>
        <Overlay animate={animate} className={cn(overlayStyles({ className: props.overlayClassName }))} />

        <ContentWrapper
          {...props}
          ref={ref}
          position={position}
          size={size}
          className={className}
          animate={animate}
          // @notes we're not requiring a description (but we require a title)
          // @see https://linear.app/benepass/issue/ENG-21554/
          aria-describedby={undefined}
        >
          {showTitle ? (
            <div className={cn('flex items-center w-full mb-4 px-7 pt-7', titleContainerClassName)}>
              {titleElement}

              {!hideCloseButton && (
                <div className={cn({ 'ml-auto': !!titleElement })}>
                  <Close aria-label="Close">
                    <CloseIcon />
                  </Close>
                </div>
              )}
            </div>
          ) : null}

          <div className={cn('overflow-y-auto flex-auto px-7', contentClassName)}>{otherElements}</div>
        </ContentWrapper>
      </Primitive.Dialog.Portal>
    );
  }
);

Content.displayName = 'Modal.Content';

export default Content;
